<script lang="ts">
  import { page } from '$app/stores';
  import type { LayoutPreference } from '$types/races';
  import { flip } from 'svelte/animate';
  import LayoutPreferenceOption from './LayoutPreferenceOption.svelte';

  $: ({ layoutConfigStore } = $page.data);
  $: activeLayoutConfig = layoutConfigStore?.activeLayoutConfig;

  // make sure the layout preferences are valid
  $: layoutPreferences = [] as LayoutPreference[];
  let index = 1;
  $: {
    layoutPreferences = [];
    $activeLayoutConfig?.layoutPreferences.forEach((preference) => {
      // check if it exists already
      if (
        layoutPreferences.find(
          (p) =>
            p.bookmaker === preference.bookmaker &&
            p.type === preference.type &&
            p.side === preference.side &&
            p.level === preference.level &&
            p.name === preference.name &&
            p.size === preference.size &&
            p.name === preference.name
        )
      ) {
        let customName = preference.name ?? preference.type.toString();
        while (layoutPreferences.find((p) => p.name === customName)) {
          customName = `${preference.name} (${index})`;
          index++;
        }
        preference.name = customName;

        if (!layoutConfigStore || !$activeLayoutConfig) {
          // console.error('no layout config');
          return;
        }

        //   update the name
        layoutConfigStore?.updateLayoutPreference(
          $activeLayoutConfig.name,
          preference
        );
      }
      layoutPreferences.push(preference);
    });
  }
  function deletePreference(event: CustomEvent) {
    if (!layoutConfigStore || !$activeLayoutConfig) {
      // console.error('no layout config');
      return;
    }
    const preference = event.detail;
    layoutConfigStore.removeLayoutPreference(
      $activeLayoutConfig.name,
      preference
    );
  }

  function updatePreference() {
    if (!layoutConfigStore || !$activeLayoutConfig) {
      // console.error('no layout config');
      return;
    }
    layoutConfigStore.updateUserLayouts();
  }

  let hovering = -1;
  function dragStart(event: DragEvent, index: number) {
    if (!event.dataTransfer) {
      return;
    }
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.dropEffect = 'move';
    const start = index;
    event.dataTransfer.setData('text/plain', start.toString());
  }
  function drop(event: DragEvent, index: number) {
    if (!layoutConfigStore || !$activeLayoutConfig) {
      // console.error('no layout config');
      return;
    }
    if (!event.dataTransfer || !$activeLayoutConfig?.layoutPreferences) {
      return;
    }
    const start = parseInt(event.dataTransfer.getData('text/plain'));
    const newOrder = [...($activeLayoutConfig?.layoutPreferences ?? [])];
    if (start < index) {
      newOrder.splice(index + 1, 0, newOrder[start]);
      newOrder.splice(start, 1);
    } else {
      newOrder.splice(index, 0, newOrder[start]);
      newOrder.splice(start + 1, 1);
    }
    layoutConfigStore.updateLayoutPreferences(
      $activeLayoutConfig.name,
      newOrder
    );
    hovering = -1;
  }
</script>

<ul class="divide-y divide-gray-200">
  {#if layoutPreferences.length}
    {#each layoutPreferences as preference, index ((preference?.bookmaker || '') + (preference?.type || '') + (preference.side || '') + (preference.level || '') + (preference.name || '') + (preference.size || ''))}
      <li
        class="cursor-move px-4 py-4 hover:bg-gray-100 sm:px-6"
        draggable="true"
        animate:flip={{ duration: 200 }}
        data-index={index}
        on:dragstart={(e) => dragStart(e, index)}
        on:dragenter={() => (hovering = index)}
        on:dragover|preventDefault
        on:drop={(e) => drop(e, index)}
        class:bg-blue-100={hovering === index}
      >
        <LayoutPreferenceOption
          {preference}
          on:delete={deletePreference}
          on:update={updatePreference}
        />
      </li>
    {/each}
  {:else}
    <div class="my-10 flex flex-row justify-center text-gray-400">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="h-6 w-6"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m0 0a2.246 2.246 0 00-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0121 12v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6c0-.98.626-1.813 1.5-2.122"
        />
      </svg>
      <p class="ml-2">Column preferences will appear here</p>
    </div>
  {/if}
</ul>
